import {useCallback, useEffect, useRef} from 'react';
import {errorMessageKey} from '../../api';
import {useRouter} from '../../app/router';
import {QR_PARAMS, ROUTES} from '../../app/routes';
import {useI18n} from '../../i18n';
import {useMenu} from '../../menu';
import {useGuestSession, useServiceStatus} from '../../session';
import {
  ActionLink,
  Button,
  Card,
  Screen,
  ScreenBody,
  ScreenFooter,
  Stack,
  TopBar,
  VenueLabel,
} from '../../ui';
import {LanguageToggle} from '../Q02LanguageToggle';

/**
 * # Q12 · Queue-ahead landing (counter / poster QR)
 *
 * Route `/q?t=<table_qr_token>&m=<tenant_id>` — a QR on a counter, a poster or
 * an A-frame, scanned by someone **standing in a line**, not seated at a table.
 *
 * ## Why this does not look like Q1
 *
 * Q1 is "you are at table 7, here is the menu". This is "you are in a queue,
 * order now and collect at the counter". Same product, different moment, and the
 * spec (03 §Q12) says explicitly they must not be confused. Three things carry
 * the difference and none of them is decoration:
 *
 *   * **No table identity anywhere.** `session.table_id` is null on this entry
 *     and `common.table` is never rendered here. A guest who reads "โต๊ะ" on a
 *     counter QR will sit down and wait for food that is coming to a hatch.
 *   * `q12.takeawayNote` is the lead line — what this entry *is*.
 *   * `q12.pickupNote` sits directly above the action — what happens *after*
 *     they order, which is the question a person in a queue actually has.
 *
 * ## Queue length and wait estimate are unknown, and that is the honest answer
 *
 * The spec asks for both. **No endpoint in the frozen contract gives a guest
 * credential either number** (ADR-0005: the contract is frozen; a change to it
 * is an escalation). So this screen renders `q12.queueUnknown` and
 * `q12.waitUnknown` and points at staff — it does not count orders it cannot
 * see, does not time-box a guess, and does not hide the row to make the screen
 * look tidier. A wrong wait estimate on a counter QR is a guest who leaves.
 *
 * `q12.queueLength` / `q12.waitEstimate` stay unused until a field lands.
 *
 * ## No photos, no green
 *
 * There is no image on this screen at all — not a missing one, not a
 * placeholder. Identity is type, spacing and the one Chili Red action.
 *
 * No money is shown, so nothing is Pandan (foundations §4). Nothing on a landing
 * screen has been verified by ReservePay and nothing here pretends otherwise.
 *
 * ## Where it sends the guest
 *
 *   no `?t=`                      → Q19 (`/invalid`) — this page is only reachable by scan
 *   session `invalid` / `expired` → Q19 (`/invalid`)
 *   venue not serving             → Q20 (`/closed`)
 *   otherwise                     → Q3 (`/menu`) on the primary action
 *
 * Every automatic hop uses `{replace: true}`: a back gesture must not land the
 * guest on a spinner that re-opens a session.
 *
 * ## REDESIGN · the same shell as every other screen
 *
 * The package does not draw this one, so it is built from the vocabulary of the
 * nine it does: a 56px top bar carrying the venue name where the back link would
 * be (there is nowhere back to — this is the first screen of the visit) and the
 * language control pushed right, a body on Sticky Rice, a flat white card with a
 * warm hairline, and one solid Chili Red action in an unbordered footer. The
 * hand-rolled bar, card, notice and button are gone; every one of them was a
 * second copy of something `src/ui` already owns.
 */
export default function Q12QueueAheadLanding() {
  const {t, ln, language} = useI18n();
  const {navigate, query} = useRouter();
  const {status, session, error, open} = useGuestSession();
  const {notServing} = useServiceStatus();
  const menu = useMenu();

  const token = (query.get(QR_PARAMS.token) ?? '').trim();
  const tenantId = (query.get(QR_PARAMS.tenant) ?? '').trim() || undefined;

  const openSession = useCallback(() => {
    if (!token) return;
    void open({
      tableQrToken: token,
      tenantId,
      // The mode is the whole point of this entry. There is no table id to pass.
      serviceMode: 'queue_ahead',
      preferredLanguage: language,
    });
  }, [open, token, tenantId, language]);

  // Once, on arrival. `open()` recognises a re-scan of the same token as a
  // resume, so a guest returning from their camera roll does not pay for a
  // second round trip; a *different* token (they scanned the counter QR while
  // holding a stale dine-in session) correctly opens a new one.
  const attempted = useRef(false);
  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;
    openSession();
  }, [openSession]);

  // No token means this URL was typed, shared or truncated — not scanned. Q19
  // owns that conversation (`q19.noQrTitle`), and it is a dead end, not an error.
  useEffect(() => {
    if (!token) navigate(ROUTES.invalid, {replace: true});
  }, [token, navigate]);

  useEffect(() => {
    if (status === 'invalid' || status === 'expired') {
      navigate(ROUTES.invalid, {replace: true});
    }
  }, [status, navigate]);

  // Unknown is never treated as closed — `useServiceStatus` only reports
  // `notServing` when the server actually said so.
  useEffect(() => {
    if (status === 'active' && notServing) navigate(ROUTES.closed, {replace: true});
  }, [status, notServing, navigate]);

  const venueName = ln(session?.merchant_display_name);
  const opening = status === 'opening' || status === 'idle';
  const failed = status === 'error';

  // The one live-projection fact that can reach a screen with no item list: the
  // menu itself became unreadable while the guest stood here. Said out loud with
  // a retry rather than discovered one tap later on an empty menu screen.
  const menuUnreadable = menu.status === 'error' && menu.menu === null;

  return (
    <Screen className="q12">
      <style href="q12-queue-ahead" precedence="medium">
        {CSS}
      </style>

      {/*
        The venue occupies the slot the back link would have, which is the menu
        screen's arrangement and is right for the same reason: there is nowhere
        to go back to from the first screen of a visit. The language control is
        the segmented one, not the pill — a counter QR is a first-contact moment
        exactly like Q1, and a lost tourist should not have to read a navigation
        label to escape a script they cannot read.
      */}
      <TopBar
        leading={venueName ? <VenueLabel name={venueName} /> : null}
        trailing={<LanguageToggle />}
      />

      <ScreenBody className="q12-body">
        <h1 className="pk-h1">{t('q12.title')}</h1>
        <p>{t('q12.takeawayNote')}</p>

        <div className="q12-status" aria-live="polite">
          {opening ? <p className="pk-muted">{t('common.loading')}</p> : null}

          {failed ? (
            <Card>
              <Stack>
                <p>{t(errorMessageKey(error))}</p>
                <Button variant="secondary" onClick={openSession}>
                  {t('common.retry')}
                </Button>
              </Stack>
            </Card>
          ) : null}
        </div>

        {/*
         * Two facts the spec asks for and the contract cannot answer. They are
         * stated as unknown, in the guest's language, with the one action that
         * does work — ask the person behind the counter.
         */}
        <Card as="section">
          <Stack>
            <p className="pk-muted">{t('q12.queueUnknown')}</p>
            <p className="pk-muted">{t('q12.waitUnknown')}</p>
            <p className="pk-strong">{t('common.askStaff')}</p>
          </Stack>
        </Card>

        {menuUnreadable ? (
          <Card>
            <Stack>
              <p>{t(errorMessageKey(menu.error))}</p>
              <Button variant="secondary" onClick={menu.reload}>
                {t('common.retry')}
              </Button>
            </Stack>
          </Card>
        ) : null}
      </ScreenBody>

      {/*
        The footer is unbordered: the body is short and does not scroll under it,
        and the design draws the rule only where content passes beneath one.
      */}
      <ScreenFooter bordered={false}>
        {/* What happens after they order, immediately above the thing that
            orders. In a queue that is the only question anyone has. */}
        <p className="pk-muted q12-centred">{t('q12.pickupNote')}</p>

        {/*
         * One solid Chili Red action on the screen (foundations §5). A real `<a>`
         * so a long-press opens a working page and a screen reader hears a link.
         *
         * Selling still does not block on a slow session or on a failed *menu*
         * read: Q3 renders its own loading and its own retry for both, so the tap
         * lands somewhere that talks.
         *
         * It DOES block on a failed session open, which it did not before. The
         * reason given was "Q3 can open a session of its own", and **Q3 cannot**:
         * it has no token, no `open()` call and no scanned QR to open one with,
         * and `MenuProvider` only fetches while the session is `active`. So a
         * guest who tapped this after a failed open arrived at a menu skeleton
         * that never resolves and never says anything — the same dead end 03 §Q9
         * rules out, reached one tap later. The retry above is the honest action
         * at that moment, and it is the only one offered.
         */}
        {failed ? null : (
          <ActionLink to={ROUTES.menu} variant="primary">
            {t('q12.startOrdering')}
          </ActionLink>
        )}

        <p className="pk-hint q12-centred">{t('common.attribution')}</p>
      </ScreenFooter>
    </Screen>
  );
}

/**
 * Screen-local CSS, in one string, hoisted once by React.
 *
 * Six rules. Custom properties only — no hex value is written here, so the till
 * and this page cannot drift into two different reds. Everything else on the
 * screen is a shared primitive or a shared class, which is the point of the
 * redesign: the card, the button, the top bar, the footer and the type scale are
 * all the same objects the other twelve screens use.
 */
const CSS = `
.q12 {
  max-width: 34rem;
  margin: 0 auto;
  width: 100%;
}

.q12-body {
  display: flex;
  flex-direction: column;
  gap: var(--pokpok-space-4);
}

/* Nothing in flight, nothing to announce, no gap left behind. */
.q12-status:empty {
  display: none;
}

.q12-centred {
  text-align: center;
}
`;
